/**
 * FitCraft Studio 统一日志系统
 * 提供分级日志记录和生产环境控制
 */

// 日志级别定义
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  SILENT: 4
};

// 日志级别名称映射
const LOG_LEVEL_NAMES = {
  0: 'DEBUG',
  1: 'INFO',
  2: 'WARN',
  3: 'ERROR',
  4: 'SILENT'
};

// 日志颜色样式
const LOG_STYLES = {
  DEBUG: 'color: #666; font-size: 12px;',
  INFO: 'color: #2196F3; font-weight: normal;',
  WARN: 'color: #FF9800; font-weight: bold;',
  ERROR: 'color: #F44336; font-weight: bold;',
  SUCCESS: 'color: #4CAF50; font-weight: bold;'
};

class Logger {
  constructor(module = 'FitCraft') {
    this.module = module;
    this.currentLevel = this.getLogLevel();
    this.maxLogEntries = 1000;
    this.logs = [];
        
    // 绑定方法到实例
    this.debug = this.debug.bind(this);
    this.info = this.info.bind(this);
    this.warn = this.warn.bind(this);
    this.error = this.error.bind(this);
    this.success = this.success.bind(this);
  }
    
  /**
     * 获取当前环境的日志级别
     */
  getLogLevel() {
    // 优先使用配置中的日志级别
    if (window.FitCraftConfig && window.FitCraftConfig.LOG_LEVEL) {
      const configLevel = window.FitCraftConfig.LOG_LEVEL.toUpperCase();
      if (LOG_LEVELS[configLevel] !== undefined) {
        return LOG_LEVELS[configLevel];
      }
    }
        
    // 根据环境自动判断
    const hostname = window.location.hostname;
        
    if (hostname === 'localhost' || 
            hostname === '127.0.0.1' || 
            hostname.startsWith('192.168.') ||
            hostname.includes('ngrok')) {
      return LOG_LEVELS.DEBUG; // 开发环境显示所有日志
    }
        
    if (hostname.includes('test') || hostname.includes('staging')) {
      return LOG_LEVELS.INFO; // 测试环境显示INFO及以上
    }
        
    return LOG_LEVELS.ERROR; // 生产环境只显示错误
  }
    
  /**
     * 格式化时间戳
     */
  getTimestamp() {
    const now = new Date();
    return `${now.toTimeString().split(' ')[0]  }.${  now.getMilliseconds().toString().padStart(3, '0')}`;
  }
    
  /**
     * 记录日志条目到内存
     */
  recordLog(level, message, data) {
    const logEntry = {
      timestamp: Date.now(),
      level: LOG_LEVEL_NAMES[level],
      module: this.module,
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : undefined
    };
        
    this.logs.push(logEntry);
        
    // 限制日志条目数量
    if (this.logs.length > this.maxLogEntries) {
      this.logs.shift();
    }
        
    // 在开发环境下，将严重错误发送到后端（仅在有API端点时）
    if (level >= LOG_LEVELS.ERROR && this.currentLevel <= LOG_LEVELS.INFO && window.location.hostname !== 'localhost') {
      this.sendErrorToBackend(logEntry);
    }
  }
    
  /**
     * 发送错误日志到后端
     */
  async sendErrorToBackend(logEntry) {
    try {
      // 避免在日志系统中再次产生日志，防止递归
      await fetch('/api/log', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...logEntry,
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      });
    } catch (error) {
      // 静默失败，避免日志系统本身产生错误
    }
  }
    
  /**
     * 通用日志方法
     */
  log(level, message, ...data) {
    if (level < this.currentLevel) {return;}
        
    const timestamp = this.getTimestamp();
    const levelName = LOG_LEVEL_NAMES[level];
    const style = LOG_STYLES[levelName] || '';
        
    // 记录到内存
    this.recordLog(level, message, data);
        
    // 格式化输出
    const prefix = `[${timestamp}] ${this.module}:${levelName}`;
        
    if (data.length > 0) {
      console.log(`%c${prefix}`, style, message, ...data);
    } else {
      console.log(`%c${prefix}`, style, message);
    }
  }
    
  /**
     * DEBUG级别日志 - 详细的调试信息
     */
  debug(message, ...data) {
    this.log(LOG_LEVELS.DEBUG, message, ...data);
  }
    
  /**
     * INFO级别日志 - 一般信息
     */
  info(message, ...data) {
    this.log(LOG_LEVELS.INFO, message, ...data);
  }
    
  /**
     * WARN级别日志 - 警告信息
     */
  warn(message, ...data) {
    this.log(LOG_LEVELS.WARN, message, ...data);
  }
    
  /**
     * ERROR级别日志 - 错误信息
     */
  error(message, ...data) {
    this.log(LOG_LEVELS.ERROR, message, ...data);
  }
    
  /**
     * SUCCESS级别日志 - 成功信息
     */
  success(message, ...data) {
    const timestamp = this.getTimestamp();
    const prefix = `[${timestamp}] ${this.module}:SUCCESS`;
        
    this.recordLog(LOG_LEVELS.INFO, `SUCCESS: ${message}`, data);
        
    if (this.currentLevel <= LOG_LEVELS.INFO) {
      if (data.length > 0) {
        console.log(`%c${prefix}`, LOG_STYLES.SUCCESS, message, ...data);
      } else {
        console.log(`%c${prefix}`, LOG_STYLES.SUCCESS, message);
      }
    }
  }
    
  /**
     * 获取日志历史
     */
  getLogs(level = null, limit = 100) {
    let logs = this.logs;
        
    if (level !== null) {
      const targetLevel = LOG_LEVELS[level.toUpperCase()];
      if (targetLevel !== undefined) {
        logs = logs.filter(log => LOG_LEVELS[log.level] >= targetLevel);
      }
    }
        
    return logs.slice(-limit);
  }
    
  /**
     * 清空日志历史
     */
  clearLogs() {
    this.logs = [];
    this.debug('日志历史已清空');
  }
    
  /**
     * 导出日志到文件
     */
  exportLogs() {
    const logs = this.getLogs();
    const logText = logs.map(log => {
      const timestamp = new Date(log.timestamp).toISOString();
      const dataStr = log.data ? ` | ${JSON.stringify(log.data)}` : '';
      return `${timestamp} [${log.level}] ${log.module}: ${log.message}${dataStr}`;
    }).join('\n');
        
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fitcraft-logs-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
        
    this.info('日志已导出到文件');
  }
    
  /**
     * 设置日志级别
     */
  setLevel(level) {
    const targetLevel = typeof level === 'string' ? LOG_LEVELS[level.toUpperCase()] : level;
    if (targetLevel !== undefined) {
      this.currentLevel = targetLevel;
      this.info(`日志级别已设置为: ${LOG_LEVEL_NAMES[targetLevel]}`);
    } else {
      this.warn('无效的日志级别:', level);
    }
  }
    
  /**
     * 获取当前日志级别
     */
  getLevel() {
    return LOG_LEVEL_NAMES[this.currentLevel];
  }
}

// 创建全局日志实例
const logger = new Logger('FitCraft');

// 为不同模块创建日志实例的工厂函数
function createLogger(module) {
  // 添加安全检查，确保在合适的时机创建
  if (typeof window === 'undefined') {
    return {
      debug: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
      success: () => {}
    };
  }
  return new Logger(module);
}

// 安全的日志获取器工厂函数
function createSafeLogger(module) {
  let loggerInstance;
  return function() {
    if (!loggerInstance && window.createLogger) {
      loggerInstance = window.createLogger(module);
    }
    return loggerInstance || {
      debug: () => {},
      info: () => {},
      warn: () => {},
      error: (...args) => console.error(`[${module}]`, ...args),
      success: () => {}
    };
  };
}

// 导出到全局作用域
window.Logger = Logger;
window.logger = logger;
window.createLogger = createLogger;
window.createSafeLogger = createSafeLogger;

// 兼容性：提供全局日志方法
window.logDebug = logger.debug;
window.logInfo = logger.info;
window.logWarn = logger.warn;
window.logError = logger.error;
window.logSuccess = logger.success;

// 开发环境下添加调试工具到window
if (logger.currentLevel <= LOG_LEVELS.DEBUG) {
  window.FitCraftDebug = {
    logger,
    getLogs: () => logger.getLogs(),
    exportLogs: () => logger.exportLogs(),
    clearLogs: () => logger.clearLogs(),
    setLogLevel: (level) => logger.setLevel(level),
    getLogLevel: () => logger.getLevel()
  };
    
  logger.debug('🔧 FitCraft Debug工具已加载到 window.FitCraftDebug');
}

logger.info('📝 FitCraft日志系统已初始化', {
  级别: logger.getLevel(),
  模块: logger.module,
  环境: window.location.hostname
});